import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  App,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Row,
  Select,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useSnackbar } from "notistack";
import { ApplicationModules, configKeys } from "../constants/modules";
import RowActions from "./RowActions";
import API from "../services/api";

const customerFields = [
  { name: "name", label: "Company Name", required: true, width: 12 },
  {
    name: "code",
    label: "Customer Code",
    required: true,
    width: 12,
    helperText: "Use 3 to 6 uppercase letters only.",
  },
  { name: "phone", label: "Phone", required: false, width: 12 },
  { name: "state", label: "State", required: false, width: 12 },
  { name: "address", label: "Address", required: false, width: 12 },
  { name: "host_name", label: "Host_Name", required: true, width: 12 },
  { name: "email", label: "Email", type: "email", required: false, width: 12 },
  {
    name: "password",
    label: "Tenant Admin Password",
    type: "password",
    required: false,
    width: 12,
    helperText: "Used for the seeded tenant admin account.",
  },
  { name: "city", label: "City", required: false, width: 12 },
];

const formatConfigValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string") return value;

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const CustomerWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { modal } = App.useApp();

  const [activeStep, setActiveStep] = useState(0);
  const [customerData, setCustomerData] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedModule, setSelectedModule] = useState(undefined);
  const [selectedKey, setSelectedKey] = useState(undefined);
  const [configValue, setConfigValue] = useState("");
  const [editingConfig, setEditingConfig] = useState(null);
  const [deletedConfigIds, setDeletedConfigIds] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [customerFormData, setCustomerFormData] = useState({});
  const [customerFormErrors, setCustomerFormErrors] = useState({});

  const initialCustomerData = location.state?.customer || null;
  const isEditMode = !!initialCustomerData;
  const steps = [{ title: "Customer Details" }, { title: "Add Configurations" }];

  useEffect(() => {
    if (isEditMode && initialCustomerData.id) {
      setCustomerData(initialCustomerData);
      setCustomerFormData(initialCustomerData);
      fetchCustomerConfigs(initialCustomerData.id);
    } else {
      const emptyForm = {};
      customerFields.forEach((field) => {
        emptyForm[field.name] = "";
      });
      setCustomerFormData(emptyForm);
    }
  }, [isEditMode, initialCustomerData]);

  const fetchCustomerConfigs = async (customerId) => {
    try {
      const response = await API.get(`/customer-configs?customer_id=${customerId}`);
      setConfigs(
        (response.data.data || []).map((config) => ({
          ...config,
          config_value: formatConfigValue(config.config_value),
          isNew: false,
        }))
      );
      setDeletedConfigIds([]);
    } catch {
      // Request failures are already captured by the shared API interceptor.
    }
  };

  const validateCustomerForm = () => {
    const errors = {};
    const isFieldRequired = (field) => field.required || (!isEditMode && field.name === "email");

    customerFields.filter(isFieldRequired).forEach((field) => {
      const value = customerFormData[field.name];
      if (!value || value.toString().trim() === "") {
        errors[field.name] = `${field.label} is required`;
      }
    });

    if (customerFormData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(customerFormData.email)) {
        errors.email = "Please enter a valid email address";
      }
    }

    if (customerFormData.code) {
      const codeRegex = /^[A-Z]{3,6}$/;
      const normalizedCode = customerFormData.code.trim().toUpperCase();
      if (!codeRegex.test(normalizedCode)) {
        errors.code = "Customer Code must contain only uppercase letters and be 3 to 6 characters long";
      }
    }

    if (customerFormData.password) {
      if (customerFormData.password.length < 6) {
        errors.password = "Password must be at least 6 characters";
      }
    }

    setCustomerFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateConfigForm = () => {
    const errors = {};
    if (!selectedModule) errors.module = "Application Module is required";
    if (!selectedKey) errors.key = "Config Key is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCustomerFormChange = (fieldName, value) => {
    const nextValue = fieldName === "code" ? value.toUpperCase() : value;
    setCustomerFormData((prev) => ({ ...prev, [fieldName]: nextValue }));
    if (customerFormErrors[fieldName]) {
      setCustomerFormErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  const handleAddConfig = () => {
    if (!validateConfigForm()) return;

    const isNewConfig = editingConfig ? editingConfig.isNew : true;
    const newConfig = {
      id: editingConfig ? editingConfig.id : `temp-${Date.now()}`,
      customer_id: customerData?.id || null,
      application_module: selectedModule,
      config_key: selectedKey,
      config_value: configValue,
      is_active: true,
      isNew: isNewConfig,
    };

    if (editingConfig) {
      setConfigs(
        configs.map((config) => (config.id === editingConfig.id ? newConfig : config))
      );
      enqueueSnackbar("Configuration updated locally", { variant: "info" });
      setEditingConfig(null);
    } else {
      setConfigs([...configs, newConfig]);
      enqueueSnackbar("Configuration added locally", { variant: "info" });
    }

    setSelectedModule(undefined);
    setSelectedKey(undefined);
    setConfigValue("");
    setFormErrors({});
  };

  const handleEditConfig = (config) => {
    setSelectedModule(config.application_module);
    setSelectedKey(config.config_key);
    setConfigValue(formatConfigValue(config.config_value));
    setEditingConfig(config);
  };

  const handleDeleteConfig = (config) => {
    modal.confirm({
      title: "Delete configuration",
      content: "Are you sure you want to delete this configuration?",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () => {
        setConfigs((prev) => prev.filter((c) => c.id !== config.id));
        if (!config.isNew && config.id) {
          setDeletedConfigIds((prev) => (prev.includes(config.id) ? prev : [...prev, config.id]));
        }

        if (editingConfig && editingConfig.id === config.id) {
          setSelectedModule(undefined);
          setSelectedKey(undefined);
          setConfigValue("");
          setEditingConfig(null);
        }

        enqueueSnackbar("Configuration removed locally", { variant: "info" });
      },
    });
  };

  const handleCancelEdit = () => {
    setSelectedModule(undefined);
    setSelectedKey(undefined);
    setConfigValue("");
    setEditingConfig(null);
    setFormErrors({});
  };

  const handleCustomerSubmit = async () => {
    if (!validateCustomerForm()) {
      enqueueSnackbar("Please fill in all required fields correctly", { variant: "error" });
      return;
    }

    setCustomerData(
      isEditMode
        ? { ...customerFormData, id: initialCustomerData.id }
        : customerFormData
    );
    enqueueSnackbar("Customer details saved locally, proceed to configurations", {
      variant: "info",
    });
    setActiveStep(1);
  };

  const handleFinalSubmit = async () => {
    if (!customerData) {
      enqueueSnackbar("No customer data to submit", { variant: "error" });
      return;
    }

    setLoading(true);
    try {
      const { password: tenantAdminPassword, ...customerPayload } = customerData;
      let customerId;

      if (isEditMode) {
        await API.put(`/customers/${initialCustomerData.id}`, customerPayload);
        customerId = initialCustomerData.id;
        enqueueSnackbar("Customer updated successfully", { variant: "success" });
      } else {
        const response = await API.post("/customers", { ...customerPayload, is_active: false });
        customerId = response.data.data.id;
        enqueueSnackbar("Customer created successfully", { variant: "success" });
      }

      if (configs.length > 0) {
        for (const config of configs) {
          const configData = {
            customer_id: customerId,
            application_module: config.application_module,
            config_key: config.config_key,
            config_value: config.config_value,
            is_active: true,
          };

          if (config.config_key === "db_connection_string") {
            configData.tenantAdmin = {
              email: customerPayload.email || "",
              userName: customerPayload.name || "",
              ...(tenantAdminPassword ? { password: tenantAdminPassword } : {}),
            };
          }

          if (config.id && !String(config.id).startsWith("temp-") && !config.isNew) {
            await API.put(`/customer-configs/${config.id}`, configData);
          } else {
            await API.post("/customer-configs", configData);
          }
        }
        enqueueSnackbar(`${configs.length} configuration(s) saved successfully`, {
          variant: "success",
        });
      }

      if (isEditMode && deletedConfigIds.length > 0) {
        for (const configId of deletedConfigIds) {
          await API.delete(`/customer-configs/${configId}`);
        }
        enqueueSnackbar(`${deletedConfigIds.length} configuration(s) deleted successfully`, {
          variant: "success",
        });
      }

      navigate("/customers");
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || error.message || "Error saving data", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 1) {
      handleFinalSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate("/customers");
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const configColumns = [
    {
      title: "Application Module",
      dataIndex: "application_module",
      key: "application_module",
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
    {
      title: "Configuration Key",
      dataIndex: "config_key",
      key: "config_key",
      render: (value) => <Typography.Text strong>{value}</Typography.Text>,
    },
    {
      title: "Configuration Value",
      dataIndex: "config_value",
      key: "config_value",
      render: (value) => {
        const displayValue = formatConfigValue(value);
        return (
          <div className="config-value-block">
            {displayValue || <em>No value set</em>}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, config) => (
        <RowActions
          placement="bottom"
          items={[
            {
              key: "edit",
              label: "Edit Configuration",
              icon: <EditOutlined />,
              onClick: () => handleEditConfig(config),
            },
            {
              key: "delete",
              label: "Delete Configuration",
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => handleDeleteConfig(config),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Card
  className="app-page-card"
  variant="borderless"
  styles={{ body: { padding: 24 } }}
>
  {/* Page Heading */}
  <div
    className="page-heading"
    style={{
      display: "flex",
      alignItems: "center",
      marginBottom: 24,
    }}
  >
    {/* Left */}
    <div style={{ width: 40 }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/customers")}
      />
    </div>

    {/* Center */}
    <div style={{ flex: 1, textAlign: "center" }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        {isEditMode ? "Edit Customer" : "Add New Customer"}
      </Typography.Title>
    </div>

    {/* Right Placeholder */}
    <div style={{ width: 40 }} />
  </div>

  <Card className="wizard-card" variant="borderless">
    <Steps current={activeStep} items={steps} />

    {activeStep === 0 && (
      <div style={{ marginTop: 32 }}>
        <Row gutter={[16, 16]}>
          {customerFields.map((field) => (
            <Col xs={24} md={12} key={field.name}>
              <div className="field-label">
                {field.label}
                {!isEditMode && field.name === "email" ? " *" : ""}
              </div>

              {field.type === "password" ? (
                <Input.Password
                  value={customerFormData[field.name] || ""}
                  onChange={(e) =>
                    handleCustomerFormChange(field.name, e.target.value)
                  }
                  status={customerFormErrors[field.name] ? "error" : ""}
                  disabled={loading}
                  placeholder={field.label}
                  autoComplete="new-password"
                />
              ) : (
                <Input
                  type={field.type === "email" ? "email" : "text"}
                  value={customerFormData[field.name] || ""}
                  onChange={(e) =>
                    handleCustomerFormChange(field.name, e.target.value)
                  }
                  status={customerFormErrors[field.name] ? "error" : ""}
                  disabled={loading}
                  placeholder={field.label}
                  maxLength={field.name === "code" ? 6 : undefined}
                />
              )}

              {field.helperText && (
                <Typography.Text
                  type="secondary"
                  style={{ display: "block", marginTop: 4, fontSize: 12 }}
                >
                  {field.helperText}
                </Typography.Text>
              )}

              {customerFormErrors[field.name] && (
                <Typography.Text
                  type="danger"
                  style={{ fontSize: 12 }}
                >
                  {customerFormErrors[field.name]}
                </Typography.Text>
              )}
            </Col>
          ))}
        </Row>

        <Space
          style={{
            width: "100%",
            justifyContent: "space-between",
            marginTop: 32,
          }}
        >
          <Button
            onClick={() => navigate("/customers")}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            type="primary"
            onClick={handleCustomerSubmit}
            disabled={loading}
          >
            Next
          </Button>
        </Space>
      </div>
    )}

    {activeStep === 1 && (
      <div style={{ marginTop: 32 }}>
        <Card variant="borderless" className="config-editor-card">
          <Space
            orientation="vertical"
            size={16}
            style={{ width: "100%" }}
          >
            <div>
              <Typography.Title level={4} style={{ marginBottom: 4 }}>
                {editingConfig
                  ? "Edit Configuration"
                  : "Add New Configuration"}
              </Typography.Title>

              <Typography.Text type="secondary">
                Add configuration settings for this customer.
              </Typography.Text>
            </div>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <div className="field-label">
                  Application Module *
                </div>

                <Select
                  value={selectedModule}
                  onChange={(value) => {
                    setSelectedModule(value);

                    if (formErrors.module) {
                      setFormErrors((prev) => ({
                        ...prev,
                        module: "",
                      }));
                    }
                  }}
                  disabled={loading}
                  status={formErrors.module ? "error" : ""}
                  placeholder="Select Application Module"
                  style={{ width: "100%" }}
                  options={Object.values(ApplicationModules).map(
                    (module) => ({
                      value: module,
                      label: module,
                    })
                  )}
                />

                {formErrors.module && (
                  <Typography.Text
                    type="danger"
                    style={{ fontSize: 12 }}
                  >
                    {formErrors.module}
                  </Typography.Text>
                )}
              </Col>

              <Col xs={24} md={12}>
                <div className="field-label">
                  Configuration Key *
                </div>

                <Select
                  value={selectedKey}
                  onChange={(value) => {
                    setSelectedKey(value);

                    if (formErrors.key) {
                      setFormErrors((prev) => ({
                        ...prev,
                        key: "",
                      }));
                    }
                  }}
                  disabled={loading || !selectedModule}
                  status={formErrors.key ? "error" : ""}
                  placeholder="Select Configuration Key"
                  style={{ width: "100%" }}
                  options={configKeys.map((key) => ({
                    value: key,
                    label: key,
                  }))}
                />

                {formErrors.key && (
                  <Typography.Text
                    type="danger"
                    style={{ fontSize: 12 }}
                  >
                    {formErrors.key}
                  </Typography.Text>
                )}
              </Col>

              <Col xs={24}>
                <div className="field-label">
                  Configuration Value
                </div>

                <Input.TextArea
                  className="configuration-value-input"
                  value={configValue}
                  onChange={(e) =>
                    setConfigValue(e.target.value)
                  }
                  disabled={loading}
                  rows={8}
                  placeholder="Configuration value"
                />
              </Col>
            </Row>

            <Space
              style={{
                width: "100%",
                justifyContent: "flex-end",
              }}
            >
              {editingConfig && (
                <Button
                  icon={<SaveOutlined />}
                  onClick={handleCancelEdit}
                  disabled={loading}
                >
                  Cancel Edit
                </Button>
              )}

              <Button
                type="primary"
                icon={
                  editingConfig ? (
                    <SaveOutlined />
                  ) : (
                    <PlusOutlined />
                  )
                }
                onClick={handleAddConfig}
                disabled={
                  loading ||
                  !selectedModule ||
                  !selectedKey
                }
              >
                {editingConfig
                  ? "Update Configuration"
                  : "Add Configuration"}
              </Button>
            </Space>
          </Space>
        </Card>

        {configs.length > 0 && (
          <Card
            variant="borderless"
            style={{ marginTop: 24 }}
            title={`Configuration Overview (${configs.length})`}
          >
            <Table
              rowKey={(config, index) =>
                config.id || index
              }
              columns={configColumns}
              dataSource={configs}
              pagination={false}
              rowClassName={(config) =>
                editingConfig?.id === config.id
                  ? "config-row config-row-active"
                  : "config-row"
              }
            />
          </Card>
        )}

        <Space
          style={{
            width: "100%",
            justifyContent: "space-between",
            marginTop: 32,
          }}
        >
          <Button
            onClick={handleBack}
            disabled={loading}
          >
            Back
          </Button>

          <Button
            type="primary"
            onClick={handleNext}
            loading={loading}
          >
            {configs.length > 0
              ? "Complete Setup"
              : "Skip & Finish"}
          </Button>
        </Space>

        {configs.length === 0 && (
          <Card
            variant="borderless"
            style={{
              marginTop: 24,
              textAlign: "center",
            }}
          >
            <Empty
              description="No configurations added yet"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button
                onClick={() =>
                  window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                  })
                }
              >
                Add Configuration Above
              </Button>
            </Empty>
          </Card>
        )}
      </div>
    )}
  </Card>
</Card>
  );
};

export default CustomerWizard;
